import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Row, Col, Card, Tag, Badge, Input, Empty, Tooltip, message } from "antd";
import { SearchOutlined, UserOutlined, ClockCircleOutlined } from "@ant-design/icons";
import Scrollbars from "react-custom-scrollbars";
import dayjs from "dayjs";
import db from "../../../../database";
import useAuthStore from "../../../../stores/useAuthStore";
import { getMultilingualText } from "../../../../utils/getMultilingualText";
import { SyncData } from "../Tables-Management/SyncData";

const STATUS_LABELS = {
  OPN: "Open",
  STD: "Seated",
  ONG: "Order Placed",
  OCU: "Bill Pending",
  FSM: "Food Served",
  BRQ: "Bill Requested",
  RES: "Reserved",
};

const STATUS_COLORS = {
  OPN: "#a7c957",
  STD: "#978ea1",
  ONG: "#f4a261",
  OCU: "#bc4749",
  FSM: "#4ade80",
  BRQ: "#e76f51",
  RES: "#f2e8cf",
};

const WaiterManagement = ({ tableCards, setTableCards, setKeyValue, setShowProducts, setSelectedTable }) => {
  const tillData = useAuthStore.getState().tillData;
  const salesReps = useMemo(() => tillData?.tillAccess?.csBunit?.salesRep || [], [tillData]);
  const [searchText, setSearchText] = useState("");
  const [selectedWaiter, setSelectedWaiter] = useState(null);
  const [assignMode, setAssignMode] = useState(false);
  const [selectedUnassignedTable, setSelectedUnassignedTable] = useState(null);

  const waiterTableMap = useMemo(() => {
    const map = {};
    salesReps.forEach((rep) => {
      map[rep.salesRepresentId] = {
        ...rep,
        tables: [],
        activeCount: 0,
        totalGuests: 0,
      };
    });

    (tableCards || []).forEach((table) => {
      if (table.statusType === "OPN") return;
      const waiterId = table.salesRepId || table.waiter;
      if (waiterId && map[waiterId]) {
        map[waiterId].tables.push(table);
        map[waiterId].activeCount += 1;
        map[waiterId].totalGuests += table.noOfPersons || 0;
      }
    });

    return map;
  }, [salesReps, tableCards]);

  const unassignedTables = useMemo(() => {
    return (tableCards || []).filter((table) => {
      if (table.statusType === "OPN") return false;
      const waiterId = table.salesRepId || table.waiter;
      return !waiterId || !salesReps.some((r) => r.salesRepresentId === waiterId);
    });
  }, [tableCards, salesReps]);

  const filteredWaiters = useMemo(() => {
    if (!searchText.trim()) return salesReps;
    const query = searchText.toLowerCase();
    return salesReps.filter((rep) => rep.name?.toLowerCase().includes(query));
  }, [salesReps, searchText]);

  const handleAssignWaiter = useCallback(
    async (waiterId, waiterName) => {
      if (!selectedUnassignedTable) return;
      const table = selectedUnassignedTable;

      const updated = {
        ...table,
        waiter: waiterName,
        salesRepId: waiterId,
        tabbleSync: "N",
      };

      try {
        await db.tableData.update(table.cwrFbTableId, {
          waiter: waiterName,
          salesRepId: waiterId,
          tabbleSync: "N",
        });
        await SyncData(updated, "upsertTableStatus");

        const allTables = await db.tableData.toArray();
        setTableCards(allTables);
        setSelectedUnassignedTable(null);
        setAssignMode(false);
        message.success(`Table ${table.name} assigned to ${waiterName}`);
      } catch (err) {
        console.error("[WaiterManagement] assign error:", err);
        message.error("Failed to assign waiter");
      }
    },
    [selectedUnassignedTable, setTableCards]
  );

  const handleReassignTable = useCallback(
    async (table, newWaiterId, newWaiterName) => {
      const updated = {
        ...table,
        waiter: newWaiterName,
        salesRepId: newWaiterId,
        tabbleSync: "N",
      };

      try {
        await db.tableData.update(table.cwrFbTableId, {
          waiter: newWaiterName,
          salesRepId: newWaiterId,
          tabbleSync: "N",
        });
        await SyncData(updated, "upsertTableStatus");

        const allTables = await db.tableData.toArray();
        setTableCards(allTables);
        message.success(`Table ${table.name} reassigned to ${newWaiterName}`);
      } catch (err) {
        console.error("[WaiterManagement] reassign error:", err);
        message.error("Failed to reassign waiter");
      }
    },
    [setTableCards]
  );

  const handleTableClick = useCallback(
    (table) => {
      if (setSelectedTable) setSelectedTable(table);
      setKeyValue("0");
      setShowProducts(true);
    },
    [setKeyValue, setShowProducts, setSelectedTable]
  );

  const getElapsedTime = (startedAt) => {
    if (!startedAt) return "";
    const start = dayjs(startedAt);
    const diffMins = dayjs().diff(start, "minute");
    if (diffMins < 60) return `${diffMins}m`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  if (!salesReps.length) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <Empty description="No waiters configured. Add sales representatives in the admin panel." />
      </div>
    );
  }

  return (
    <div style={{ padding: "12px 16px", height: "93vh", backgroundColor: "#f5f5f5" }}>
      {/* Header */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", gap: "8px" }}>
        <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#2F3856" }}>
          {getMultilingualText("Waiter Management")}
        </h2>
        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap", flex: "1 1 200px", justifyContent: "flex-end" }}>
          <Input
            placeholder="Search waiters..."
            prefix={<SearchOutlined style={{ color: "#bbb" }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ maxWidth: 220, minWidth: 120, flex: "1 1 120px", borderRadius: 8 }}
            allowClear
          />
          <div style={{ display: "flex", gap: "6px", alignItems: "center", fontSize: "11px", color: "#888" }}>
            <Badge color="#a7c957" text="Open" />
            <Badge color="#f4a261" text="Active" />
            <Badge color="#bc4749" text="Billing" />
          </div>
        </div>
      </div>

      <Scrollbars autoHide style={{ height: "85vh" }}>
        {/* Unassigned tables section */}
        {unassignedTables.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 600, color: "#e76f51" }}>
                Unassigned Tables ({unassignedTables.length})
              </h3>
              {!assignMode && (
                <button
                  onClick={() => setAssignMode(true)}
                  style={{
                    background: "#2F3856",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "6px 14px",
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                >
                  Assign Waiter
                </button>
              )}
              {assignMode && (
                <button
                  onClick={() => {
                    setAssignMode(false);
                    setSelectedUnassignedTable(null);
                  }}
                  style={{
                    background: "#ccc",
                    color: "#333",
                    border: "none",
                    borderRadius: 6,
                    padding: "6px 14px",
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
            <Row gutter={[10, 10]}>
              {unassignedTables.map((table) => (
                <Col key={table.cwrFbTableId} xs={8} sm={6} md={4} lg={3}>
                  <Card
                    size="small"
                    hoverable
                    onClick={() => {
                      if (assignMode) {
                        setSelectedUnassignedTable(table);
                      } else {
                        handleTableClick(table);
                      }
                    }}
                    style={{
                      borderRadius: 8,
                      border:
                        assignMode && selectedUnassignedTable?.cwrFbTableId === table.cwrFbTableId
                          ? "2px solid #2F3856"
                          : "1px solid #eee",
                      backgroundColor: STATUS_COLORS[table.statusType] || "#f5f5f5",
                      cursor: "pointer",
                    }}
                    bodyStyle={{ padding: "8px 10px" }}
                  >
                    <div style={{ fontWeight: 600, fontSize: "13px", color: "#333" }}>{table.name}</div>
                    <Tag color="orange" style={{ fontSize: "10px", marginTop: 4 }}>
                      {STATUS_LABELS[table.statusType] || table.statusType}
                    </Tag>
                    {table.guestName && (
                      <div style={{ fontSize: "10px", color: "#666", marginTop: 2 }}>{table.guestName}</div>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Waiter selection for assign mode */}
            {assignMode && selectedUnassignedTable && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "12px",
                  background: "#fff",
                  borderRadius: 8,
                  border: "1px solid #2F3856",
                }}
              >
                <div style={{ fontSize: "13px", fontWeight: 500, marginBottom: "8px" }}>
                  Assign <strong>{selectedUnassignedTable.name}</strong> to:
                </div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {salesReps.map((rep) => (
                    <button
                      key={rep.salesRepresentId}
                      onClick={() => handleAssignWaiter(rep.salesRepresentId, rep.name)}
                      style={{
                        background: "#2F3856",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 16px",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      {rep.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Waiter cards grid */}
        <Row gutter={[16, 16]}>
          {filteredWaiters.map((rep) => {
            const waiterData = waiterTableMap[rep.salesRepresentId] || { tables: [], activeCount: 0, totalGuests: 0 };
            const isSelected = selectedWaiter === rep.salesRepresentId;

            return (
              <Col key={rep.salesRepresentId} xs={24} sm={12} md={8} lg={6}>
                <Card
                  hoverable
                  onClick={() => setSelectedWaiter(isSelected ? null : rep.salesRepresentId)}
                  style={{
                    borderRadius: 10,
                    border: isSelected ? "2px solid #2F3856" : "1px solid #e8e8e8",
                    boxShadow: isSelected ? "0 4px 12px rgba(47,56,86,0.15)" : "0 1px 3px rgba(0,0,0,0.08)",
                    transition: "all 0.2s",
                  }}
                  bodyStyle={{ padding: "14px 16px" }}
                >
                  {/* Waiter header */}
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: waiterData.activeCount > 0 ? "#2F3856" : "#d9d9d9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: "16px",
                        fontWeight: 600,
                      }}
                    >
                      {rep.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: "14px", color: "#333" }}>{rep.name}</div>
                      <div style={{ fontSize: "11px", color: "#999" }}>
                        {waiterData.activeCount} table{waiterData.activeCount !== 1 ? "s" : ""} &middot;{" "}
                        {waiterData.totalGuests} guest{waiterData.totalGuests !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <Badge
                      count={waiterData.activeCount}
                      style={{
                        backgroundColor: waiterData.activeCount > 3 ? "#bc4749" : waiterData.activeCount > 0 ? "#2F3856" : "#d9d9d9",
                      }}
                    />
                  </div>

                  {/* Table summary chips */}
                  {waiterData.tables.length > 0 ? (
                    <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                      {waiterData.tables.slice(0, isSelected ? undefined : 4).map((table) => (
                        <Tag
                          key={table.cwrFbTableId}
                          color={
                            table.statusType === "OCU" || table.statusType === "BRQ"
                              ? "red"
                              : table.statusType === "ONG"
                              ? "orange"
                              : "blue"
                          }
                          style={{ fontSize: "10px", margin: "2px 0", cursor: "pointer", borderRadius: 4 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTableClick(table);
                          }}
                        >
                          {table.name}
                        </Tag>
                      ))}
                      {!isSelected && waiterData.tables.length > 4 && (
                        <Tag style={{ fontSize: "10px", margin: "2px 0", borderRadius: 4 }}>
                          +{waiterData.tables.length - 4} more
                        </Tag>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontSize: "11px", color: "#ccc", fontStyle: "italic" }}>No active tables</div>
                  )}

                  {/* Expanded view when selected */}
                  {isSelected && waiterData.tables.length > 0 && (
                    <div style={{ marginTop: "12px", borderTop: "1px solid #f0f0f0", paddingTop: "10px" }}>
                      {waiterData.tables.map((table) => (
                        <div
                          key={table.cwrFbTableId}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "6px 8px",
                            marginBottom: "4px",
                            borderRadius: 6,
                            backgroundColor: "#fafafa",
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTableClick(table);
                          }}
                        >
                          <div>
                            <span style={{ fontWeight: 600, fontSize: "12px" }}>{table.name}</span>
                            {table.guestName && (
                              <span style={{ fontSize: "11px", color: "#888", marginLeft: 6 }}>
                                ({table.guestName})
                              </span>
                            )}
                          </div>
                          <div style={{ display: "flex", gap: "4px", alignItems: "center", flexShrink: 0 }}>
                            {table.startedAt && (
                              <Tooltip title={dayjs(table.startedAt).format("hh:mm A")}>
                                <span style={{ fontSize: "10px", color: "#999" }}>
                                  <ClockCircleOutlined style={{ marginRight: 2 }} />
                                  {getElapsedTime(table.startedAt)}
                                </span>
                              </Tooltip>
                            )}
                            <Tag
                              color={
                                table.statusType === "OCU" || table.statusType === "BRQ"
                                  ? "red"
                                  : table.statusType === "ONG"
                                  ? "orange"
                                  : "blue"
                              }
                              style={{ fontSize: "10px", borderRadius: 4, margin: 0 }}
                            >
                              {STATUS_LABELS[table.statusType] || table.statusType}
                            </Tag>
                            {/* Reassign dropdown */}
                            <Tooltip title="Reassign table">
                              <select
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  const newId = e.target.value;
                                  if (!newId) return;
                                  const newRep = salesReps.find((r) => r.salesRepresentId === newId);
                                  if (newRep) handleReassignTable(table, newId, newRep.name);
                                }}
                                value=""
                                style={{
                                  width: 32,
                                  height: 32,
                                  minWidth: 32,
                                  opacity: 0.6,
                                  cursor: "pointer",
                                  border: "none",
                                  background: "transparent",
                                  fontSize: "12px",
                                }}
                              >
                                <option value="">&#8635;</option>
                                {salesReps
                                  .filter((r) => r.salesRepresentId !== rep.salesRepresentId)
                                  .map((r) => (
                                    <option key={r.salesRepresentId} value={r.salesRepresentId}>
                                      {r.name}
                                    </option>
                                  ))}
                              </select>
                            </Tooltip>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>

        {/* Summary bar */}
        <div
          style={{
            marginTop: "16px",
            padding: "10px 12px",
            background: "#fff",
            borderRadius: 10,
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-around",
            gap: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          }}
        >
          <div style={{ textAlign: "center", flex: "1 1 60px", minWidth: 60 }}>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#2F3856" }}>{salesReps.length}</div>
            <div style={{ fontSize: "10px", color: "#888" }}>Waiters</div>
          </div>
          <div style={{ textAlign: "center", flex: "1 1 60px", minWidth: 60 }}>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#f4a261" }}>
              {(tableCards || []).filter((t) => t.statusType !== "OPN").length}
            </div>
            <div style={{ fontSize: "10px", color: "#888" }}>Active</div>
          </div>
          <div style={{ textAlign: "center", flex: "1 1 60px", minWidth: 60 }}>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#bc4749" }}>{unassignedTables.length}</div>
            <div style={{ fontSize: "10px", color: "#888" }}>Unassigned</div>
          </div>
          <div style={{ textAlign: "center", flex: "1 1 60px", minWidth: 60 }}>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#a7c957" }}>
              {Object.values(waiterTableMap).reduce((sum, w) => sum + w.totalGuests, 0)}
            </div>
            <div style={{ fontSize: "10px", color: "#888" }}>Guests</div>
          </div>
        </div>
      </Scrollbars>
    </div>
  );
};

export default WaiterManagement;
